import type { PayloadAction, SerializedError } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { nvFetch } from '../../shared/helpers/nvFetch';
import { setActiveCustomerAndFetchLinkedUsers } from '../../shared/reducers/client';
import { finaliseLogout } from '../../shared/store/thunks';
import { ApiCall } from '../../shared/types/Call';
import type { ThunkAPI } from '../store/types';

type FetchResult = { newCalls: ApiCall[]; isForced: boolean };

export interface FetchCallHistoryParams {
  endpointId?: string;
  page?: number;
  createdAfter?: string;
}

interface CallHistoryState {
  calls: ApiCall[];
  isFetching: boolean;
  error?: SerializedError;
  visibilityFilter: 'SHOW_CALLS' | 'SHOW_ENDPOINTS';
  searchString: string;
  params: {
    includeLocal: boolean;
    pageSize: number;
    page: number;
  };
  newestCallCreatedTime?: string;
  reachedEnd: boolean;
  scrolledPage: number;
}

const initialState: CallHistoryState = {
  calls: [],
  isFetching: false,
  error: undefined,
  visibilityFilter: 'SHOW_CALLS',
  searchString: '',
  params: {
    includeLocal: true,
    pageSize: 40,
    page: 0,
  },
  reachedEnd: false,
  scrolledPage: 0,
};

export const fetchCallHistory = createAsyncThunk<
  FetchResult,
  { overrideParams?: FetchCallHistoryParams; force?: boolean },
  ThunkAPI
>(
  'callHistory/fetch',
  async ({ overrideParams = {}, force = false }, thunkAPI) => {
    const state = thunkAPI.getState();
    const { callHistory } = state;
    const customerId = state.client.activeCustomer?.id;
    const id = state.client.extension?.id;

    let myURI: string;

    if (!overrideParams.endpointId) {
      myURI = id?.replace(/^0+/, '') || '';
    } else {
      myURI = overrideParams.endpointId.replace(/^0+/, '');
    }

    const correctDirection = (calls: ApiCall[]) => {
      return calls.map((call) => {
        if (call.srcEndpoint === myURI) {
          call.direction = 'OUT';
        } else {
          call.direction = 'IN';
        }
        return call;
      });
    };

    const { params } = callHistory;

    const updatedParams: Record<string, string | number | boolean> = {
      ...params,
      page: callHistory.scrolledPage + 1,
      endpointId: myURI,
      ...overrideParams,
    };

    // Limit to last 7 days
    const weekAgoTs = Date.now() - 1000 * 60 * 60 * 24 * 7;
    updatedParams.startedAfter = new Date(weekAgoTs).toISOString();

    thunkAPI.dispatch(
      callHistorySlice.actions.fetchCallHistoryStarted(updatedParams),
    );

    const fetchRes = await nvFetch(`/customers/${customerId}/calls`, {
      params: updatedParams,
    });
    const res: ApiCall[] = correctDirection(
      (await fetchRes.json()).items || [],
    );

    return { newCalls: res, isForced: force };
  },
  {
    condition: ({ force, overrideParams = {} }, thunkAPI) => {
      const state = thunkAPI.getState();
      const { callHistory } = state;
      const id = state.client.extension?.id;

      if (callHistory.isFetching || (!force && callHistory.reachedEnd)) {
        return false;
      }

      if (!id && !overrideParams.endpointId) {
        return false;
      }
    },
  },
);

export const fetchNewCalls = createAsyncThunk<void, void, ThunkAPI>(
  'callHistory/fetchNewCalls',
  (_, thunkAPI) => {
    const {
      callHistory: { newestCallCreatedTime },
    } = thunkAPI.getState();

    const params: FetchCallHistoryParams = {
      page: 1,
    };

    if (newestCallCreatedTime) {
      params.createdAfter = newestCallCreatedTime;
    }

    thunkAPI.dispatch(
      fetchCallHistory({ overrideParams: params, force: true }),
    );
  },
);

const callHistorySlice = createSlice({
  name: 'callHistory',
  initialState,
  reducers: {
    fetchCallHistoryStarted: (state, action) => {
      const params = action.payload;
      // keep track of the highest page we have scrolled to
      let scrolledPage = state.scrolledPage;
      if (params.page > scrolledPage) {
        scrolledPage = params.page;
      }
      state.isFetching = true;
      state.params = params;
      state.scrolledPage = scrolledPage;
    },
    visibilityFilterSet: (
      state,
      action: PayloadAction<{ filter: 'SHOW_CALLS' | 'SHOW_ENDPOINTS' }>,
    ) => {
      state.visibilityFilter = action.payload.filter;
    },
    // FIXME those names...
    callHistorySearched: (
      state,
      action: PayloadAction<{ searchString: string }>,
    ) => {
      state.searchString = action.payload.searchString;
    },
    callHistoryReset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCallHistory.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.error;
      })
      .addCase(
        fetchCallHistory.fulfilled,
        (state, action: PayloadAction<FetchResult>) => {
          const calls = [...state.calls];
          const { newCalls, isForced } = action.payload;
          const callsMap = new Map();

          // No new calls!
          if (newCalls.length <= 0) {
            state.isFetching = false;
            state.error = undefined;
            state.reachedEnd = !isForced ? true : state.reachedEnd;
            return;
          }

          /**
           * Check to see if the srcEndpoint of one of the calls in
           * the call history is the same as one of the calls in
           * the newly fetched calls. If it's not, it means we've
           * fetched a new extensions set of calls, so drop the
           * old calls and just use the newly fetched calls
           */
          /* if (calls.length > 0 && newCalls.length > 0) {
            const oldSrcEndpoint = calls[0].srcEndpoint;
            const newSrcEndpoint = newCalls[0].srcEndpoint;
            if (newSrcEndpoint !== oldSrcEndpoint) {
              return {
                ...state,
                calls: newCalls,
                isFetching: false,
                error: null,
                reachedEnd: false
              };
            }
          } */

          // Create a map of current calls, id to index
          calls.forEach((c, i) => callsMap.set(c.id, i));

          // Use the above map to ensure we don't
          // insert duplicate calls
          newCalls.forEach((n) => {
            const callsIdx = callsMap.get(n.id);
            if (typeof callsIdx === 'undefined') {
              calls.push(n);
            }
          });

          // Sort the calls by callStarted (descending)
          calls.sort((a, b) => {
            const keyA = new Date(a.callStarted);
            const keyB = new Date(b.callStarted);
            // Compare the 2 dates
            if (keyA < keyB) return 1;
            if (keyA > keyB) return -1;
            return 0;
          });

          state.calls = calls;
          state.isFetching = false;
          state.error = undefined;
          state.newestCallCreatedTime = calls[0].created;
        },
      )
      .addCase(finaliseLogout.fulfilled, () => initialState)
      .addCase(
        setActiveCustomerAndFetchLinkedUsers.fulfilled,
        () => initialState,
      );
  },
});

export const { visibilityFilterSet, callHistorySearched, callHistoryReset } =
  callHistorySlice.actions;

export default callHistorySlice.reducer;
